import {
  BotFormType,
  ConfirmBotModalStep,
  ConfirmOption,
  EditBotOption,
} from "@shared/features/bots/BotForm/common/types";
import { useContext, useEffect, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotData, BotStatus, BotType, LanguageNameSpaces } from "@shared/types";
import { BOT_TYPE_TO_DISPLAY_NAME } from "@shared/features/bots/constants";
import { isSymbol } from "@shared/types/utils";
import { capitalizeFirstLetter } from "@shared/common/utils";
import { useValidateBotName } from "@shared/features/bots/BotForm/common/hooks";
import useHedgingExposureBreakdown from "@shared/features/bots/BotForm/common/hooks/useHedgingExposureBreakdown";
import { toFixed } from "@shared/utils/numbers";
import { useBranding } from "@shared/utils/hooks/useBranding";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import pluralize from "pluralize";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useConfirmBotModalProps {
  saveChanges: (
    activate?: boolean,
    editBotOption?: EditBotOption,
    botName?: string
  ) => Promise<BotData[] | void> | undefined;
  onSuccess?: (botData?: BotData) => void;
  closeModal: () => void;
  isOpen: boolean;
  gridBotValuesChanged?: boolean;
  isMobile?: boolean;
}

export const useConfirmBotModal = ({
  saveChanges,
  onSuccess,
  closeModal,
  isOpen,
  gridBotValuesChanged,
  isMobile,
}: useConfirmBotModalProps) => {
  const {
    botGeneralData,
    isMarginExchange,
    isChannelConfiguration,
    botType,
    botFormType,
    account,
    symbols,
    initialBotAmountData,
    bulkActionValidBotsData,
    subGroup,
  } = useContext(BotFormContext)!;
  const hedgingBreakdown = useHedgingExposureBreakdown();
  const isOnboarding = !!useContext(OnboardingContext);
  const { getQuantityCoin } = useSymbolsMetadata();
  const [step, setStep] = useState<ConfirmBotModalStep>(
    ConfirmBotModalStep.SELECT_ACTIVATION
  );
  const [confirmOption, setConfirmOption] = useState<ConfirmOption>(
    ConfirmOption.CREATE_AND_ACTIVATE
  );
  const [editOption, setEditOption] = useState<EditBotOption>(
    EditBotOption.OVERWRITE_EXISTING
  );
  const [botName, setBotName] = useState<string>();
  const [freeTrialModalOpen, setFreeTrialModalOpen] = useState(false);
  const { nameError } = useValidateBotName(botName);
  const shouldDisplayNameInput =
    step === ConfirmBotModalStep.EDIT_OPTION &&
    editOption === EditBotOption.CREATE_NEW;
  const { brandingData } = useBranding();

  const isEdit = botFormType === BotFormType.EDIT;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isCreate = !isEdit && botFormType !== BotFormType.BULK_EDIT;
  const isDcaBot = botType === BotType.ASAP;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot =
    botType === BotType.TRADING_VIEW || isTradingViewSignalsBot;
  const isSignalsBot = botType === BotType.SIGNAL;
  const showSteps = isTradingViewBot && isCreate && !isBulkCreate;
  const botStatus =
    isEdit && botGeneralData && "status" in botGeneralData
      ? botGeneralData.status
      : undefined;
  const isActiveBot = botStatus === BotStatus.ACTIVE;
  const isEditActiveBotWarning =
    step === ConfirmBotModalStep.EDIT_ACTIVE_WARNING;
  const isEditGridBot = isGridBot && isEdit;
  const isEditActiveGridBot = isEditGridBot && isActiveBot;

  const setDefaultStep = () => {
    let step = ConfirmBotModalStep.SELECT_ACTIVATION;
    if (isEditGridBot && gridBotValuesChanged) {
      if (isEditActiveGridBot) step = ConfirmBotModalStep.EDIT_ACTIVE_WARNING;
      else step = ConfirmBotModalStep.EDIT_OPTION;
    }
    setStep(step);
  };

  const saveChangesOrOpenFreeTrialModal = () => {
    if (subGroup?.isFreeTrialGroup) {
      closeModal();
      setTimeout(() => setFreeTrialModalOpen(true), isMobile ? 500 : 0);
    } else {
      handleSaveChanges();
    }
  };

  const onOk = async () => {
    if (step === ConfirmBotModalStep.EDIT_ACTIVE_WARNING) {
      setStep(ConfirmBotModalStep.EDIT_OPTION);
    } else if (step === ConfirmBotModalStep.EDIT_OPTION) {
      if (isEditActiveGridBot) {
        handleSaveChanges();
      } else {
        setStep(ConfirmBotModalStep.SELECT_ACTIVATION);
      }
    } else if (step === ConfirmBotModalStep.SELECT_ACTIVATION) {
      saveChangesOrOpenFreeTrialModal();
    }
  };

  useEffect(() => {
    if (isOpen) {
      setDefaultStep();
    }
  }, [isOpen]);

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const botTypeDisplay = t(BOT_TYPE_TO_DISPLAY_NAME[botType]);
  const quantityCoin =
    !isSymbol(account) && !isSymbol(symbols)
      ? getQuantityCoin(symbols[0], account?.exchange)
      : undefined;

  const baseActionName = isCreate ? "create" : "save";
  const capitalizedActionName = capitalizeFirstLetter(baseActionName);
  const pluralizedBotText = pluralize(
    "bot",
    (isBulkCreate && bulkActionValidBotsData?.length) || 1
  );

  const getActivationOptions = () => {
    let activeText = `The ${pluralizedBotText} will immediately open a trade`;
    if (botType === BotType.SIGNAL)
      activeText = `The ${pluralizedBotText} will open a trade when a new signal is posted`;
    if (isTradingViewBot)
      activeText = `The ${pluralizedBotText} will open a ${
        isTradingViewSignalsBot ? "signal" : "trade"
      } when triggered`;
    else if (isGridBot) {
      activeText = `The ${pluralizedBotText} will immediately start trading and buy ${initialBotAmountData.initialUsdAmount} USD worth of ${quantityCoin}`;
    } else if (isHedgingBot) {
      if (hedgingBreakdown) {
        const { longRatio, shortRatio, longAmountUsd, shortAmountUsd } =
          hedgingBreakdown;
        activeText = `The ${pluralizedBotText} will immediately start trading and use a hedging of ${longRatio}% long (${toFixed(
          longAmountUsd,
          2
        )} USD) and ${shortRatio}% short (${toFixed(shortAmountUsd, 2)} USD)`;
      }
    } else if (isChannelConfiguration) {
      activeText = `The ${
        isChannelConfiguration ? "configuration" : pluralizedBotText
      } will be set as the group default and will be immediately available to channel members`;
    }
    let inactiveText = `The ${
      isChannelConfiguration ? "configuration" : "bot"
    } will ${isCreate ? "be created" : "remain"} in an ${
      isChannelConfiguration ? "unpublished" : "inactive"
    } state`;
    if (isSignalsBot && brandingData?.modified_create_bot_button_texts) {
      activeText = `Selecting ${
        isCreate ? "Create" : "Save"
      } & Activate will automatically open new trades on your account every time a new signal is published, using your saved configuration. You can deactivate the bot at any time.`;
      inactiveText = `Selecting ${isCreate ? "Create" : "Save"} Only will ${
        isCreate ? "save" : "leave"
      } the bot in an inactive state. The bot will not open trades automatically and can be used to manually follow signals and create trades on your account.`;
    }
    return [
      {
        selected: confirmOption === ConfirmOption.CREATE_AND_ACTIVATE,
        value: ConfirmOption.CREATE_AND_ACTIVATE,
        title: `${capitalizedActionName} and ${
          isChannelConfiguration ? "Publish" : "Activate"
        }`,
        subText: activeText,
        onClick: () => setConfirmOption(ConfirmOption.CREATE_AND_ACTIVATE),
      },
      {
        selected: confirmOption === ConfirmOption.CREATE_ONLY,
        value: ConfirmOption.CREATE_ONLY,
        title: `${capitalizedActionName} Only`,
        subText: inactiveText,
        onClick: () => setConfirmOption(ConfirmOption.CREATE_ONLY),
      },
    ];
  };

  const getEditOptions = () => {
    return [
      {
        selected: editOption === EditBotOption.OVERWRITE_EXISTING,
        value: EditBotOption.OVERWRITE_EXISTING,
        title: "Overwrite existing bot",
        subText:
          "Overwriting this bot will have all of it's related statistics, trades and information reset",
        onClick: () => setEditOption(EditBotOption.OVERWRITE_EXISTING),
      },
      {
        selected: editOption === EditBotOption.CREATE_NEW,
        value: EditBotOption.CREATE_NEW,
        title: "Create a new bot instance",
        subText: "Create a new bot with the updated configuration",
        onClick: () => setEditOption(EditBotOption.CREATE_NEW),
      },
    ];
  };

  const getConfirmOptions = () => {
    if (step === ConfirmBotModalStep.SELECT_ACTIVATION && !isOnboarding)
      return getActivationOptions();
    else if (step === ConfirmBotModalStep.EDIT_OPTION) return getEditOptions();
  };

  const getActivationContent = () => {
    if (isOnboarding) {
      const content = isSignalsBot
        ? "By proceeding, a trade will be automatically opened for you as soon as a signal will be posted."
        : "By proceeding, a trade will be automatically opened for you.";
      return {
        areYouSure: undefined,
        generalContent: [content],
        leverageWarning: undefined,
      };
    }
    return {
      areYouSure: `Do you wish to ${
        isChannelConfiguration ? "publish" : "activate"
      } the ${pluralizedBotText}?`,
      generalContent: undefined,
      // TODO: for what other types does this need to show?
      leverageWarning:
        isDcaBot &&
        isMarginExchange &&
        `The bot you’re about to ${
          isCreate ? "create" : "save"
        } is leveraged and will be deactivated if a trade results in liquidation.`,
    };
  };

  const getEditActiveWarningContent = () => {
    return {
      areYouSure: "Are you sure you want to edit this bot?",
      generalContent: [
        `Saving your edits when the grid bot is still active will cause the bot to close the current open trade and sell your open position at market order and then open a new trade immediately buying ${initialBotAmountData.initialUsdAmount} USD worth of ${quantityCoin}.`,
      ],
      leverageWarning: undefined,
    };
  };

  const getContentData = () => {
    if (step === ConfirmBotModalStep.SELECT_ACTIVATION)
      return getActivationContent();
    else if (isEditActiveBotWarning) return getEditActiveWarningContent();
  };

  const actionName = isCreate ? "Create" : "Edit";

  const getTitle = () => {
    return isEditActiveBotWarning
      ? "Edit Active Grid Bot"
      : `${actionName} ${
          isBulkCreate ? `${bulkActionValidBotsData?.length} ` : ""
        }${botTypeDisplay} ${capitalizeFirstLetter(pluralizedBotText)}`;
  };

  const handleSaveChanges = () => {
    saveChanges(
      isCreate || !isActiveBot
        ? confirmOption === ConfirmOption.CREATE_AND_ACTIVATE
        : undefined,
      isEditGridBot ? editOption : undefined,
      shouldDisplayNameInput ? botName : undefined
    )
      ?.then((response) => {
        if (showSteps) {
          setStep(ConfirmBotModalStep.GET_UUIDS);
        } else {
          onSuccess?.(response?.[0]);
        }
      })
      .catch(() => {});
  };

  const onCancel = () => {
    setStep(ConfirmBotModalStep.SELECT_ACTIVATION);
    setConfirmOption(ConfirmOption.CREATE_AND_ACTIVATE);
    setEditOption(EditBotOption.OVERWRITE_EXISTING);
    if (step === ConfirmBotModalStep.GET_UUIDS) {
      onSuccess?.();
    } else {
      closeModal();
    }
  };

  const successMessage = `${botTypeDisplay} Bot ${
    isCreate ? "Created" : "Updated"
  } Successfully!`;

  const radioGroupData = {
    value:
      step === ConfirmBotModalStep.SELECT_ACTIVATION
        ? confirmOption
        : editOption,
    setValue:
      step === ConfirmBotModalStep.SELECT_ACTIVATION
        ? setConfirmOption
        : setEditOption,
    options: getConfirmOptions(),
  };

  const nameInputData = {
    shouldDisplay: shouldDisplayNameInput,
    title: "Bot Name",
    value: botName,
    onChange: setBotName,
    error: nameError,
  };

  const freeTrialModalData = {
    isOpen: freeTrialModalOpen,
    onCancel: () => {
      setFreeTrialModalOpen(false);
      onCancel();
    },
    saveChanges: handleSaveChanges,
  };

  const getOkText = () => {
    if (isEditActiveBotWarning) return "Yes";
    else if (subGroup?.isFreeTrialGroup) return "Continue";
    return "Save";
  };

  return {
    title: getTitle(),
    showSteps,
    steps: ["Create Bot", "Get UUIDs"],
    parsedStep: step === ConfirmBotModalStep.GET_UUIDS ? 1 : 0,
    contentData: getContentData(),
    successMessage,
    radioGroupData,
    okText: getOkText(),
    cancelText: isEditActiveBotWarning ? "No" : undefined,
    isOkButtonDisabled: shouldDisplayNameInput && !!nameError,
    onOk,
    onCancel,
    step,
    footer: step === ConfirmBotModalStep.GET_UUIDS ? false : undefined,
    nameInputData,
    freeTrialModalData,
  };
};

export default useConfirmBotModal;
